import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  Undo2,
  Loader2,
  ExternalLink,
  X,
  Search,
  Users,
  User,
  Flag,
} from 'lucide-react';
import {
  api,
  parseApiError,
  type FlagSeverity,
  type DisciplineFlag,
  type DisciplineFlagStatus,
  type MatchSummary,
} from '../lib/api';
import { isAdminRole, clanRoleDisplayName } from '../lib/roles';
import { isOrgManagerOfAny, managedOrgIds } from '../lib/orgAccess';
import { useEffectiveMe } from '../lib/viewAs';
import { cn } from '../lib/cn';
import { usePageActions } from '../lib/pageActions';
import { Card } from '../components/Card';
import { PageWrapper } from '../components/PageWrapper';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { displayClanName } from '../lib/displayName';
import {
  SEVERITY_ORDER,
  SEVERITY_META,
  formatDate,
  URL_RE,
  StatusChip,
} from '../lib/disciplineFlagDisplay';

// ─────────────────────────────────────────────────────────────────────────────
// DisciplinePage — the dedicated /admin/discipline surface (13-discipline-
// admin-page). DisciplineFlagPanel stays embedded on PlayerCard/ClanDetail as
// in-context shortcuts (product owner's call, kept as-is); this page is the
// "one place" view on top: a real target PICKER (pick any member or clan in an
// org, not just the one you happen to be looking at), and the whole org's
// disciplinary history in a single filterable list — no hunting per clan.
//
// Severity/status visual language is SHARED with DisciplineFlagPanel via
// lib/disciplineFlagDisplay (single source for colours/labels, so the two
// surfaces can never visually drift). The row-with-actions layout below is
// its own copy — the panel's row assumes a single known target and doesn't
// need a target-type/name line, so factoring the whole row into one shared
// component would couple the already-shipped v1 panel to this page's shape
// for no real benefit. Keep this list in sync by eye if the panel's row
// layout changes.
//
// Admin/founder, OR an ORG_ADMIN/ORG_OWNER of at least one org
// (org-admin de-gate, 2026-07-17) — scoped to their own org(s) via
// managedOrgIds. Gated on the view-as-adjusted `me` (useEffectiveMe) so a
// founder previewing "Org admin (scoped)" sees exactly this surface fire for
// their simulated org, mirroring TournamentSetup.tsx/ClanManagement.tsx's own
// de-gate exactly. Non-admins/non-org-managers get a plain message and NONE
// of the discipline data (org list, clans, members, flags) is ever fetched or
// rendered for them. The backend (canManageDisciplineForOrg in
// portalApi/index.ts) is the REAL enforcement — this is UX gating only.
// ─────────────────────────────────────────────────────────────────────────────

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// CompetitionMembersPanel/UserLookup — the displayName comment below says
// "never a raw id", so the last-resort fallback must be short, not the raw
// snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

type TargetType = 'player' | 'clan';
type StatusFilter = 'all' | DisciplineFlagStatus;
type SeverityFilter = 'all' | FlagSeverity;

export function DisciplinePage() {
  const qc = useQueryClient();

  // ── ALL hooks declared unconditionally, before any early return (same rule
  // DisciplineFlagPanel documents — the admin gate below must never skip one) ──
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  // Org-admin de-gate: read the view-as-adjusted `me` (Sidebar.tsx / lib/
  // viewAs.ts's useEffectiveMe) so a founder previewing "Org admin (scoped)"
  // sees exactly the org-scoped bypass a real scoped org admin would, and a
  // founder previewing a plain lower platform role correctly loses it
  // (identity no-op when no view-as is active — real, non-previewing
  // behaviour is unchanged).
  const effectiveMe = useEffectiveMe(me);
  const isPlatformAdmin = isAdminRole(effectiveMe?.role);
  // isOrgManagerOfAny/managedOrgIds mirror TournamentSetup.tsx/
  // ClanManagement.tsx's own org-admin de-gate exactly (founder/admin
  // short-circuit lives INSIDE the helper). canAccess replaces the old
  // isAdmin-only gate everywhere below — an org manager now reaches this
  // page too, scoped to their own org(s).
  const isOrgManager = isOrgManagerOfAny(effectiveMe);
  const canAccess = isPlatformAdmin || isOrgManager;

  const orgsQuery = useQuery({ queryKey: ['orgs'], queryFn: api.orgs, enabled: canAccess });
  // Scope the org selector to the orgs this caller actually manages.
  // managedOrgIds returns 'all' for founder/platform-admin (unfiltered,
  // unchanged); otherwise the bare orgIds of the caller's own ORG_ADMIN/
  // ORG_OWNER membership(s). This is UX scoping only — the backend
  // (canManageDisciplineForOrg) re-checks per request regardless.
  const managedIds = managedOrgIds(effectiveMe);
  const orgs = managedIds === 'all'
    ? (orgsQuery.data ?? [])
    : (orgsQuery.data ?? []).filter(o => managedIds.includes(o.id.replace(/^ORG#/, '')));
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const orgId = selectedOrgId || orgs[0]?.id || '';

  const orgClansQuery = useQuery({
    queryKey: ['orgClans', orgId],
    queryFn: () => api.orgClans(orgId),
    enabled: canAccess && !!orgId,
  });
  const clans = [...(orgClansQuery.data?.memberClans ?? [])].sort((a, b) =>
    displayClanName(a.name).localeCompare(displayClanName(b.name))
  );

  // ── Issue form: target picker + severity + reason + evidence ───────────────
  const [targetType, setTargetType] = useState<TargetType>('player');
  const [pickedClan, setPickedClan] = useState('');
  const [pickedMemberId, setPickedMemberId] = useState('');
  const [severity, setSeverity] = useState<FlagSeverity | null>(null);
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [formError, setFormError] = useState('');

  // Member list only needs fetching for the player flow (clan flow stops at
  // the clan picker), and only once a clan is picked.
  const clanMembersQuery = useQuery({
    queryKey: ['clanMembers', pickedClan],
    queryFn: () => api.clanMembers(pickedClan),
    enabled: canAccess && targetType === 'player' && !!pickedClan,
  });
  const clanMembers = clanMembersQuery.data ?? [];
  const pickedMember = clanMembers.find(m => m.discordId === pickedMemberId);

  // targetId/targetName resolve differently per flow: clan target IS the
  // clan (targetId = clan name, matching ClanDetail's DisciplineFlagPanel
  // usage exactly); player target is the picked member's discordId, with the
  // resolved Discord display name (the clan-members endpoint now returns
  // displayName; gamertag/id only as last-resort fallbacks — never a raw id).
  const targetId = targetType === 'clan' ? pickedClan : pickedMemberId;
  const targetDisplayName =
    targetType === 'clan' ? pickedClan : (pickedMember?.displayName ?? pickedMember?.gamertag ?? (pickedMember ? shortId(pickedMember.discordId) : ''));

  const onOrgChange = (id: string) => {
    setSelectedOrgId(id);
    setPickedClan('');
    setPickedMemberId('');
    setFormError('');
  };

  const onTargetTypeChange = (t: TargetType) => {
    setTargetType(t);
    setPickedMemberId('');
    setFormError('');
  };

  const onClanPick = (name: string) => {
    setPickedClan(name);
    setPickedMemberId('');
    setFormError('');
  };

  // ── Org-wide history — list(orgId) with NO targetType/targetId scopes to
  // the WHOLE org, which is exactly the "one place, no searching per clan" ask ──
  const historyQuery = useQuery({
    queryKey: ['disciplineFlags', orgId, 'org-wide'],
    queryFn: () => api.admin.disciplineFlags.list(orgId),
    enabled: canAccess && !!orgId,
  });

  const invalidateHistory = () => qc.invalidateQueries({ queryKey: ['disciplineFlags'] });

  const issueMut = useMutation({
    mutationFn: () =>
      api.admin.disciplineFlags.issue(orgId, {
        targetType,
        targetId,
        severity: severity!,
        reason: reason.trim(),
        evidence: evidence.trim() || undefined,
      }),
    onSuccess: () => {
      setSeverity(null);
      setReason('');
      setEvidence('');
      setFormError('');
      setPickedClan('');
      setPickedMemberId('');
      invalidateHistory();
    },
    onError: (e: Error) => setFormError(parseApiError(e).message || 'Failed to issue flag.'),
  });

  const [overturningSk, setOverturningSk] = useState<string | null>(null);
  const [overturnReasonText, setOverturnReasonText] = useState('');
  const [actionError, setActionError] = useState<{ sk: string; msg: string } | null>(null);

  const cosignMut = useMutation({
    mutationFn: (sk: string) => api.admin.disciplineFlags.cosign(orgId, sk),
    onSuccess: () => {
      setActionError(null);
      invalidateHistory();
    },
    onError: (e: Error, sk) => setActionError({ sk, msg: parseApiError(e).message || 'Failed to co-sign.' }),
  });

  const overturnMut = useMutation({
    mutationFn: ({ sk, reason: r }: { sk: string; reason: string }) =>
      api.admin.disciplineFlags.overturn(orgId, sk, r),
    onSuccess: () => {
      setActionError(null);
      setOverturningSk(null);
      setOverturnReasonText('');
      invalidateHistory();
    },
    onError: (e: Error, vars) => setActionError({ sk: vars.sk, msg: parseApiError(e).message || 'Failed to overturn.' }),
  });

  // ── Filters — client-side over the org-wide list ────────────────────────────
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [search, setSearch] = useState('');

  const allFlags = useMemo(
    () => [...(historyQuery.data?.flags ?? [])].sort((a, b) => b.createdAt - a.createdAt),
    [historyQuery.data]
  );
  const pendingCount = allFlags.filter(f => f.status === 'pending').length;

  const filteredFlags = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allFlags.filter(f => {
      if (statusFilter !== 'all' && f.status !== statusFilter) return false;
      if (severityFilter !== 'all' && f.severity !== severityFilter) return false;
      if (q) {
        const hay = `${f.targetName ?? ''} ${f.targetId} ${f.reason}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allFlags, statusFilter, severityFilter, search]);

  const canIssue = !!severity && reason.trim().length > 0 && !!orgId && !!targetId && !issueMut.isPending;

  // Global bottom action-bar registration — the SAME "Issue" submit as the
  // button inside the "Issue a flag" card below (same canIssue gate, same
  // issueMut.mutate() call). Gated on canAccess, this page's own admin/
  // org-manager gate, so a caller who'd hit the "Admins only" message never
  // gets a bar entry either (see lib/pageActions.tsx's role-awareness
  // contract). Hook must sit before the early-return gate (Rules of Hooks);
  // everything it reads is already computed by this point.
  const issueActions = useMemo(
    () => (canAccess ? [{
      id: 'discipline-issue',
      label: 'Issue flag',
      icon: ShieldAlert,
      onClick: () => issueMut.mutate(),
      disabled: !canIssue,
    }] : null),
    [canAccess, canIssue, issueMut.mutate],
  );
  usePageActions(issueActions);

  // ── Gate: non-admins/non-org-managers see a plain message, nothing
  // discipline-related ever renders or fetches for them ───────────────────────
  if (!me || !canAccess) {
    return (
      <PageWrapper maxWidth="3xl">
        <Card className="text-center">
          <p className="text-ink-secondary">
            Admins only. Discipline records are restricted to admin, founder, and org owner/admin roles.
          </p>
        </Card>
      </PageWrapper>
    );
  }

  const noOrgsAvailable = !orgsQuery.isLoading && orgs.length === 0;

  return (
    <PageWrapper maxWidth="4xl" spacing="lg">
      {/* Header + org selector — every action below scopes to this org */}
      <div className={cn('flex flex-wrap items-center justify-between gap-3', SETTLE)} style={settleDelay(0)}>
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-7 w-7 text-forge-400" strokeWidth={2.5} />
          <div>
            <h1 className="text-2xl font-bold text-white">Discipline</h1>
            <p className="text-xs text-ink-meta">
              Issue and review disciplinary flags for any member or clan, org-wide, in one place.
            </p>
          </div>
        </div>

        {orgs.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
              Org
            </label>
            <select value={orgId} onChange={e => onOrgChange(e.target.value)} className="input text-sm">
              {orgs.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {noOrgsAvailable && (
        <Card className="text-center">
          <p className="text-sm text-ink-secondary">You need an org to manage discipline.</p>
        </Card>
      )}

      {!!orgId && (
        <div className={cn('space-y-6', SETTLE)} style={settleDelay(1)}>
          {/* ── Issue a flag — target picker + severity + reason + evidence ── */}
          <Card>
            <h2 className="mb-4 flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
              <ShieldAlert className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
              Issue a flag
            </h2>

            {/* target-type toggle */}
            <div className="mb-4">
              <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                Target type
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onTargetTypeChange('player')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-2 border px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider transition-colors',
                    targetType === 'player'
                      ? 'border-forge-500 bg-iron-900 text-white'
                      : 'border-iron-700/60 bg-iron-950 text-iron-300 hover:border-iron-600'
                  )}
                >
                  <User className="h-3.5 w-3.5" strokeWidth={2.5} /> Player
                </button>
                <button
                  type="button"
                  onClick={() => onTargetTypeChange('clan')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-2 border px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider transition-colors',
                    targetType === 'clan'
                      ? 'border-forge-500 bg-iron-900 text-white'
                      : 'border-iron-700/60 bg-iron-950 text-iron-300 hover:border-iron-600'
                  )}
                >
                  <Users className="h-3.5 w-3.5" strokeWidth={2.5} /> Clan
                </button>
              </div>
            </div>

            {/* clan picker — both flows start here (clan = the target itself;
                player = the clan to search within for a member) */}
            <div className="mb-3">
              <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                Clan <span className="text-forge-400">*</span>
              </label>
              <select
                value={pickedClan}
                onChange={e => onClanPick(e.target.value)}
                disabled={orgClansQuery.isLoading}
                className="input w-full disabled:opacity-40"
              >
                <option value="">
                  {orgClansQuery.isLoading
                    ? 'Loading clans…'
                    : clans.length === 0
                      ? 'No clans in this org'
                      : 'Select a clan…'}
                </option>
                {clans.map(c => (
                  <option key={c.clanId} value={c.name}>{displayClanName(c.name)}</option>
                ))}
              </select>
            </div>

            {/* player picker — player flow only, once a clan is picked */}
            {targetType === 'player' && (
              <div className="mb-4">
                <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                  Member <span className="text-forge-400">*</span>
                </label>
                <select
                  value={pickedMemberId}
                  onChange={e => { setPickedMemberId(e.target.value); setFormError(''); }}
                  disabled={!pickedClan || clanMembersQuery.isLoading}
                  className="input w-full disabled:opacity-40"
                >
                  <option value="">
                    {!pickedClan
                      ? 'Pick a clan first…'
                      : clanMembersQuery.isLoading
                        ? 'Loading members…'
                        : clanMembers.length === 0
                          ? 'No members in this clan'
                          : 'Select a member…'}
                  </option>
                  {clanMembers.map(m => (
                    <option key={m.discordId} value={m.discordId}>
                      {m.displayName ?? m.gamertag ?? shortId(m.discordId)} — {clanRoleDisplayName(m.scopedRole)}
                    </option>
                  ))}
                </select>
                {pickedMember && (
                  <p className="mt-1.5 font-mono text-[10px] text-iron-500">
                    Flagging {pickedMember.displayName ?? pickedMember.gamertag ?? shortId(pickedMember.discordId)}
                  </p>
                )}
              </div>
            )}

            {targetType === 'clan' && pickedClan && (
              <p className="mb-4 font-mono text-[10px] text-iron-500">
                Flagging clan {displayClanName(pickedClan)}
              </p>
            )}

            {/* severity — same colour chips as DisciplineFlagPanel (shared) */}
            <div className="mb-3">
              <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                Severity <span className="text-forge-400">*</span>
              </label>
              <div className="flex gap-2">
                {SEVERITY_ORDER.map(sv => {
                  const meta = SEVERITY_META[sv];
                  const selected = severity === sv;
                  return (
                    <button
                      key={sv}
                      type="button"
                      onClick={() => setSeverity(sv)}
                      className={cn(
                        'flex-1 border px-2 py-2 text-center transition-colors',
                        selected
                          ? 'border-forge-500 bg-iron-900'
                          : 'border-iron-700/60 bg-iron-950 hover:border-iron-600'
                      )}
                    >
                      <span className={cn('mb-1.5 block h-3 w-full', meta.swatch)} />
                      <span className="font-display text-[9px] font-black uppercase tracking-wider text-iron-300">
                        {sv}
                      </span>
                    </button>
                  );
                })}
              </div>
              {(severity === 'red' || severity === 'black') && (
                <p className="mt-2 flex items-start gap-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-400/90">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2.5} />
                  {SEVERITY_META[severity].label} flags need a second admin's co-sign before they take effect.
                </p>
              )}
            </div>

            <div className="mb-3">
              <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                Reason <span className="text-forge-400">*</span>
              </label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="What happened…"
                rows={3}
                className="input w-full resize-none"
              />
            </div>

            <div className="mb-3">
              <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
                Evidence <span className="font-normal normal-case tracking-normal text-iron-500">(optional — URL or note)</span>
              </label>
              <input
                value={evidence}
                onChange={e => setEvidence(e.target.value)}
                placeholder="https://discord.com/channels/… or a short note"
                className="input w-full"
              />
            </div>

            {formError && (
              <div className="mb-3 flex items-center gap-2 border border-red-900/40 bg-red-950/40 px-3 py-2 text-xs text-red-300">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                {formError}
              </div>
            )}

            <button
              type="button"
              onClick={() => issueMut.mutate()}
              disabled={!canIssue}
              className={cn(
                'flex items-center gap-1.5 border border-forge-500/60 bg-forge-600/30 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-forge-500/40 disabled:cursor-not-allowed disabled:opacity-40',
                MORPH
              )}
            >
              {issueMut.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
              ) : (
                <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2.5} />
              )}
              Issue{targetDisplayName ? ` on ${targetType === 'clan' ? displayClanName(targetDisplayName) : targetDisplayName}` : ''}
            </button>
          </Card>

          {/* ── Forfeit a fixture — clan target only. Own Card, but reuses the
              target picker's pickedClan above (Lisa's ask: "when a clan is
              the selected target, offer a way to forfeit one of that clan's
              unplayed division fixtures"). `key={pickedClan}` forces a clean
              remount (fresh compId/confirm state) on every clan switch —
              same idiom as the dual-mode-form-needs-keys rule (a swapped-in
              component at the same tree position otherwise reuses state). ──*/}
          {targetType === 'clan' && pickedClan && (
            <ForfeitFixturePanel key={pickedClan} orgId={orgId} clanName={pickedClan} invalidateHistory={invalidateHistory} />
          )}

          {/* ── Org-wide history + filters ── */}
          <Card>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <h2 className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-iron-400">
                History — whole org
              </h2>
              {!historyQuery.isLoading && (
                <span className="font-mono text-[10px] text-iron-500">
                  {allFlags.length} on record{pendingCount > 0 && ` · ${pendingCount} pending co-sign`}
                </span>
              )}
            </div>

            <div className="mb-4 grid gap-2 sm:grid-cols-3">
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                className="input text-sm"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending co-sign</option>
                <option value="overturned">Overturned</option>
              </select>
              <select
                value={severityFilter}
                onChange={e => setSeverityFilter(e.target.value as SeverityFilter)}
                className="input text-sm"
              >
                <option value="all">All severities</option>
                {SEVERITY_ORDER.map(sv => (
                  <option key={sv} value={sv}>{sv}</option>
                ))}
              </select>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-iron-500" strokeWidth={2.5} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search target or reason…"
                  className="input w-full pl-8 text-sm"
                />
              </div>
            </div>

            {historyQuery.isLoading ? (
              <div className="flex items-center gap-2 py-6 text-iron-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-xs">Loading…</span>
              </div>
            ) : filteredFlags.length === 0 ? (
              <p className="py-4 text-center font-mono text-[11px] uppercase tracking-wider text-iron-500">
                {allFlags.length === 0 ? 'No flags on record for this org.' : 'No flags match these filters.'}
              </p>
            ) : (
              <div className="space-y-2">
                {filteredFlags.map(flag => (
                  <DisciplineHistoryRow
                    key={flag.sk}
                    flag={flag}
                    cosignMut={cosignMut}
                    overturnMut={overturnMut}
                    overturningSk={overturningSk}
                    setOverturningSk={setOverturningSk}
                    overturnReasonText={overturnReasonText}
                    setOverturnReasonText={setOverturnReasonText}
                    actionError={actionError}
                    setActionError={setActionError}
                  />
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </PageWrapper>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DisciplineHistoryRow — one flag in the org-wide list. Severity swatch,
// status chip, date formatting, and evidence-link detection are all shared
// with DisciplineFlagPanel via lib/disciplineFlagDisplay; the target-type/
// name line and the surrounding row chrome are specific to this page (the
// panel already knows its single target from props, so it doesn't need one).
// ─────────────────────────────────────────────────────────────────────────────

function DisciplineHistoryRow({
  flag,
  cosignMut,
  overturnMut,
  overturningSk,
  setOverturningSk,
  overturnReasonText,
  setOverturnReasonText,
  actionError,
  setActionError,
}: {
  flag: DisciplineFlag;
  cosignMut: { mutate: (sk: string) => void; isPending: boolean; variables: string | undefined };
  overturnMut: {
    mutate: (vars: { sk: string; reason: string }) => void;
    isPending: boolean;
    variables: { sk: string; reason: string } | undefined;
  };
  overturningSk: string | null;
  setOverturningSk: (sk: string | null) => void;
  overturnReasonText: string;
  setOverturnReasonText: (v: string) => void;
  actionError: { sk: string; msg: string } | null;
  setActionError: (v: { sk: string; msg: string } | null) => void;
}) {
  const meta = SEVERITY_META[flag.severity];
  const isOverturned = flag.status === 'overturned';
  const isPending = flag.status === 'pending';
  const rowActionError = actionError?.sk === flag.sk ? actionError.msg : null;
  const isOverturningThis = overturningSk === flag.sk;
  const cosignBusy = cosignMut.isPending && cosignMut.variables === flag.sk;
  const overturnBusy = overturnMut.isPending && overturnMut.variables?.sk === flag.sk;
  const TargetIcon = flag.targetType === 'clan' ? Users : User;

  return (
    <div
      className={cn(
        'border px-3 py-2.5',
        isOverturned ? 'border-iron-800/50 bg-iron-950 opacity-70' : cn(meta.border, meta.softBg)
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('h-3.5 w-3.5 shrink-0', meta.swatch)} />
        <span
          className={cn(
            'font-display text-[10px] font-black uppercase tracking-wider',
            isOverturned ? 'text-iron-500' : meta.text
          )}
        >
          {flag.severity}
        </span>
        <StatusChip status={flag.status} />
        <span className="ml-auto font-mono text-[10px] text-iron-500">
          {formatDate(flag.createdAt)}
        </span>
      </div>

      <div className="mt-1.5 flex items-center gap-1.5">
        <TargetIcon className="h-3.5 w-3.5 shrink-0 text-iron-400" strokeWidth={2.5} />
        <span className="font-display text-xs font-black uppercase tracking-wide text-iron-200">
          {flag.targetType === 'clan' ? 'Clan' : 'Player'} · {flag.targetName ?? (flag.targetType === 'player' ? shortId(flag.targetId) : flag.targetId)}
        </span>
      </div>

      <p className={cn('mt-1.5 text-sm', isOverturned ? 'text-iron-500 line-through decoration-iron-700' : 'text-iron-100')}>
        {flag.reason}
      </p>

      {flag.evidence && (
        URL_RE.test(flag.evidence) ? (
          <a
            href={flag.evidence}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 font-mono text-[11px] text-forge-300 underline hover:text-forge-200"
          >
            Evidence <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
          </a>
        ) : (
          <p className="mt-1 font-mono text-[11px] text-iron-500">
            Evidence: {flag.evidence}
          </p>
        )
      )}

      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-iron-500">
        Issued by {flag.issuedByName ?? shortId(flag.issuedBy)}
        {flag.cosignedByName && ` · co-signed by ${flag.cosignedByName}`}
      </p>

      {isOverturned && (
        <p className="mt-1.5 border-t border-iron-800/50 pt-1.5 font-mono text-[10px] text-iron-500">
          Overturned by {flag.overturnedByName ?? shortId(flag.overturnedBy ?? '')}
          {flag.overturnReason && <>: <span className="italic">{flag.overturnReason}</span></>}
        </p>
      )}

      {rowActionError && (
        <div className="mt-2 flex items-center gap-2 border border-red-900/40 bg-red-950/40 px-2.5 py-1.5 text-xs text-red-300">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          {rowActionError}
        </div>
      )}

      {!isOverturned && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {isPending && (
            <button
              type="button"
              onClick={() => { setActionError(null); cosignMut.mutate(flag.sk); }}
              disabled={cosignBusy}
              className="flex items-center gap-1.5 border border-emerald-500/50 bg-emerald-950/30 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-emerald-200 transition-colors hover:border-emerald-400 hover:bg-emerald-900/40 disabled:opacity-40"
            >
              {cosignBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
              ) : (
                <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
              )}
              Co-sign
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setActionError(null);
              setOverturningSk(isOverturningThis ? null : flag.sk);
              setOverturnReasonText('');
            }}
            className="flex items-center gap-1.5 border border-iron-700 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-iron-500 hover:text-white"
          >
            <Undo2 className="h-3 w-3" strokeWidth={2.5} />
            Overturn
          </button>
        </div>
      )}

      {isOverturningThis && (
        <div className="mt-2 border-t border-iron-800/50 pt-2">
          <label className="mb-1 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
            Reason for overturning <span className="text-forge-400">*</span>
          </label>
          <textarea
            value={overturnReasonText}
            onChange={e => setOverturnReasonText(e.target.value)}
            placeholder="Why is this being overturned…"
            rows={2}
            className="input w-full resize-none"
            autoFocus
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => { setOverturningSk(null); setOverturnReasonText(''); }}
              className="flex items-center gap-1 border border-iron-700 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-400 hover:border-iron-500 hover:text-white"
            >
              <X className="h-3 w-3" strokeWidth={2.5} />
              Cancel
            </button>
            <button
              type="button"
              onClick={() => overturnMut.mutate({ sk: flag.sk, reason: overturnReasonText.trim() })}
              disabled={!overturnReasonText.trim() || overturnBusy}
              className="flex items-center gap-1.5 border border-red-500/50 bg-red-950/30 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-red-200 transition-colors hover:border-red-400 hover:bg-red-900/40 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {overturnBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
              ) : (
                <Undo2 className="h-3 w-3" strokeWidth={2.5} />
              )}
              Confirm overturn
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ForfeitFixturePanel — admin FORFEIT action (2026-07-18). Forfeiting a team
// is a disciplinary action (POST /matches/:matchId/forfeit ALSO writes a
// discipline flag against the forfeited clan in the SAME request — see
// api.ts's forfeitMatch doc comment), so it lives on THIS page rather than a
// match-management surface.
//
// "This clan's forfeitable fixtures" has no clean resolution: ClanDetail
// carries no comp/match info, and there is no clan->competitions endpoint.
// Fallback flow (per the brief): admin picks the comp (api.orgCompetitions,
// scoped to the org already selected above), then this panel lists that
// comp's own matches (api.matches — the SAME endpoint + ['matches', compId]
// queryKey Matches.tsx/AdminPanel.tsx/MatchScheduleBoard.tsx already share,
// so invalidating it here also refreshes those surfaces) filtered to
// fixtures that (a) involve the selected clan and (b) are NOT yet CONFIRMED.
// No stageType/stageId filter is applied on top of that — the backend forfeit
// route imposes none (it works on any match, see index.ts's doc comment), and
// narrowing further client-side would risk hiding a legitimate fixture from
// the admin for no real safety gain.
// ─────────────────────────────────────────────────────────────────────────────

// clan1/clan2 on a MATCH# row are bare/qualified names (index.ts stamps
// clanId.replace(/^CLAN#/, '') everywhere a match is created) but a
// multi-team qualified side ("Beta (Alpha)") can only be matched back to the
// org's clan list via clan1Id/clan2Id (M2 epic — see MatchSummary.clan1Id's
// doc comment in api.ts). Prefers the Id field when present; always returns
// the VERBATIM clan1/clan2 string forfeitMatch must echo back as
// forfeitedClan (the server compares it verbatim against the match row).
function matchClanSide(m: MatchSummary, clanName: string): string | undefined {
  const strip = (id?: string) => (id ?? '').replace(/^CLAN#/, '');
  const side1Matches = m.clan1Id ? strip(m.clan1Id) === clanName : m.clan1 === clanName;
  if (side1Matches) return m.clan1;
  const side2Matches = m.clan2Id ? strip(m.clan2Id) === clanName : m.clan2 === clanName;
  if (side2Matches) return m.clan2;
  return undefined;
}

function ForfeitFixturePanel({
  orgId,
  clanName,
  invalidateHistory,
}: {
  orgId: string;
  clanName: string;
  invalidateHistory: () => void;
}) {
  const qc = useQueryClient();
  const [compId, setCompId] = useState('');
  const [confirmingMatchId, setConfirmingMatchId] = useState<string | null>(null);
  const [forfeitError, setForfeitError] = useState('');

  const compsQuery = useQuery({
    queryKey: ['orgCompetitions', orgId],
    queryFn: () => api.orgCompetitions(orgId),
    enabled: !!orgId,
  });
  const comps = compsQuery.data ?? [];

  const matchesQuery = useQuery({
    queryKey: ['matches', compId],
    queryFn: () => api.matches(compId),
    enabled: !!compId,
  });

  const forfeitableMatches = useMemo(
    () =>
      (matchesQuery.data ?? [])
        .filter(m => m.status !== 'CONFIRMED' && matchClanSide(m, clanName) !== undefined)
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)),
    [matchesQuery.data, clanName]
  );

  const forfeitMut = useMutation({
    mutationFn: (vars: { matchId: string; forfeitedClan: string }) =>
      api.forfeitMatch(vars.matchId, compId, vars.forfeitedClan),
    onSuccess: () => {
      setForfeitError('');
      setConfirmingMatchId(null);
      // The forfeit route writes BOTH a resolved match AND a discipline flag
      // in one request — invalidate both surfaces so the record shows up
      // here (history, below) and the fixture drops off this list.
      invalidateHistory();
      qc.invalidateQueries({ queryKey: ['matches', compId] });
    },
    onError: (e: Error) => setForfeitError(parseApiError(e).message || 'Failed to forfeit.'),
  });

  const clanLabel = displayClanName(clanName);

  return (
    <Card>
      <h2 className="mb-1 flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
        <Flag className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
        Forfeit a fixture
      </h2>
      <p className="mb-4 text-xs text-ink-secondary">
        Resolves an unplayed fixture as a walkover loss for {clanLabel} — the opponent is credited the
        win, and it also logs a disciplinary record against {clanLabel} in the history below.
      </p>

      <div className="mb-3">
        <label className="mb-1.5 block font-display text-[9px] font-black uppercase tracking-[0.2em] text-iron-500">
          Competition
        </label>
        <select
          value={compId}
          onChange={e => { setCompId(e.target.value); setConfirmingMatchId(null); setForfeitError(''); }}
          disabled={compsQuery.isLoading}
          className="input w-full disabled:opacity-40"
        >
          <option value="">
            {compsQuery.isLoading
              ? 'Loading competitions…'
              : comps.length === 0
                ? 'No competitions in this org'
                : 'Select a competition…'}
          </option>
          {comps.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {!!compId && (
        matchesQuery.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-iron-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Loading fixtures…</span>
          </div>
        ) : forfeitableMatches.length === 0 ? (
          <p className="py-3 text-center font-mono text-[11px] uppercase tracking-wider text-iron-500">
            No unplayed fixtures for {clanLabel} in this competition.
          </p>
        ) : (
          <div className="space-y-2">
            {forfeitableMatches.map(m => {
              const side = matchClanSide(m, clanName);
              if (!side) return null;
              const opponent = side === m.clan1 ? m.clan2 : m.clan1;
              const opponentLabel = displayClanName(opponent);
              const isConfirmingThis = confirmingMatchId === m.sk;
              const busy = forfeitMut.isPending && forfeitMut.variables?.matchId === m.sk;

              return (
                <div key={m.sk} className="border border-iron-700/60 bg-iron-950 px-3 py-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-display text-xs font-black uppercase tracking-wide text-iron-200">
                        vs {opponentLabel}
                      </p>
                      <p className="font-mono text-[10px] text-iron-500">
                        {m.status}{m.scheduledAt ? ` · ${formatDate(m.scheduledAt)}` : ''}
                      </p>
                    </div>
                    {!isConfirmingThis && (
                      <button
                        type="button"
                        onClick={() => { setConfirmingMatchId(m.sk); setForfeitError(''); }}
                        className={cn(
                          'flex items-center gap-1.5 border border-red-500/50 bg-red-950/30 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-red-200 transition-colors hover:border-red-400 hover:bg-red-900/40',
                          MORPH
                        )}
                      >
                        <Flag className="h-3 w-3" strokeWidth={2.5} />
                        Forfeit
                      </button>
                    )}
                  </div>

                  {isConfirmingThis && (
                    <div className="mt-2 border-t border-iron-800/50 pt-2">
                      <p className="mb-2 flex items-start gap-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-400/90">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2.5} />
                        Confirm: {clanLabel} forfeits to {opponentLabel} by walkover — this resolves the
                        fixture AND logs a disciplinary flag against {clanLabel}.
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => { setConfirmingMatchId(null); setForfeitError(''); }}
                          className="flex items-center gap-1 border border-iron-700 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-400 hover:border-iron-500 hover:text-white"
                        >
                          <X className="h-3 w-3" strokeWidth={2.5} />
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => forfeitMut.mutate({ matchId: m.sk, forfeitedClan: side })}
                          disabled={busy}
                          className={cn(
                            'flex items-center gap-1.5 border border-red-500/50 bg-red-950/30 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-red-200 transition-colors hover:border-red-400 hover:bg-red-900/40 disabled:cursor-not-allowed disabled:opacity-40',
                            MORPH
                          )}
                        >
                          {busy ? (
                            <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                          ) : (
                            <Flag className="h-3 w-3" strokeWidth={2.5} />
                          )}
                          Confirm forfeit
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {forfeitError && (
        <div className="mt-3 flex items-center gap-2 border border-red-900/40 bg-red-950/40 px-3 py-2 text-xs text-red-300">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          {forfeitError}
        </div>
      )}
    </Card>
  );
}
